import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

export default function About() {
  return (
    <>
      <Helmet>
        <title>About Us — NirmalRam Construction</title>
        <meta
          name="description"
          content="Learn about NirmalRam Construction, founded by Rambhau Patil and co-founded by Hitesh Patil (B.Tech Civil 2021) in Nandurbar, Maharashtra. 5+ years, 10+ dream homes."
        />
      </Helmet>

      <section
        style={{
          background: "#0A0A0A",
          padding: "8rem 0 4rem",
          borderBottom: "1px solid rgba(201,168,76,0.12)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div className="grid-bg" />
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="section-tag">Our Story</span>
            <h1
              style={{
                fontFamily: "Cormorant Garamond,serif",
                marginBottom: "1rem",
              }}
            >
              Built on Trust,
              <br />
              Raised on Skill
            </h1>
            <div className="section-divider" />
            <p style={{ color: "#8A8070", maxWidth: "600px", lineHeight: 1.8 }}>
              NirmalRam Construction was born from a simple belief — that every
              family deserves a well-built, beautiful home. Since 2022, we have
              brought that belief to life across Nandurbar and beyond.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founders */}
      <section className="section">
        <div className="container">
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <span className="section-tag">The Team</span>
            <h2 style={{ fontFamily: "Cormorant Garamond,serif" }}>
              Meet Our Founders
            </h2>
            <div
              className="section-divider"
              style={{ margin: "1rem auto 0" }}
            />
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))",
              gap: "2rem",
              maxWidth: "800px",
              margin: "0 auto",
            }}
          >
            {[
              {
                initials: "RP",
                name: "Rambhau Patil",
                role: "Founder",
                bio: "Retired professor with a lifelong passion for quality construction. Rambhau Patil brings academic discipline, ethical values, and decades of wisdom to every project. His vision was to build homes that generations would be proud of.",
                tags: ["Founder", "Ex-Professor", "Visionary", "Est. 2022"],
              },
              {
                initials: "HP",
                name: "Hitesh Patil",
                role: "Co-Founder & Technical Head",
                bio: "Son of Rambhau Patil, Hitesh Patil holds a B.Tech in Civil Engineering (2021). He leads all technical operations, on-site management, and client consultations, bringing modern engineering techniques to the family business.",
                tags: [
                  "B.Tech Civil (2021)",
                  "Co-Founder",
                  "Site Manager",
                  "Tech Lead",
                ],
              },
            ].map((person) => (
              <motion.div
                key={person.name}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                whileHover={{ borderColor: "rgba(201,168,76,0.4)" }}
                style={{
                  background: "#0E0E0E",
                  border: "1px solid rgba(201,168,76,0.15)",
                  padding: "2.5rem",
                  borderRadius: "3px",
                  textAlign: "center",
                  transition: "border-color 0.3s",
                }}
              >
                <div
                  style={{
                    width: "90px",
                    height: "90px",
                    borderRadius: "50%",
                    background: "rgba(201,168,76,0.1)",
                    border: "2px solid #C9A84C",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "Cormorant Garamond,serif",
                    fontSize: "2rem",
                    color: "#C9A84C",
                    margin: "0 auto 1.5rem",
                  }}
                >
                  {person.initials}
                </div>
                <h3 style={{ fontSize: "1.4rem", marginBottom: "0.35rem" }}>
                  {person.name}
                </h3>
                <div
                  style={{
                    fontSize: "0.75rem",
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    color: "#C9A84C",
                    marginBottom: "1rem",
                  }}
                >
                  {person.role}
                </div>
                <p
                  style={{
                    color: "#8A8070",
                    fontSize: "0.9rem",
                    lineHeight: 1.8,
                    marginBottom: "1.5rem",
                  }}
                >
                  {person.bio}
                </p>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                    justifyContent: "center",
                  }}
                >
                  {person.tags.map((t) => (
                    <span key={t} className="badge badge-gold">
                      {t}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section
        style={{
          background: "#0A0A0A",
          borderTop: "1px solid rgba(201,168,76,0.1)",
          padding: "5rem 0",
        }}
      >
        <div className="container">
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <span className="section-tag">Our Journey</span>
            <h2 style={{ fontFamily: "Cormorant Garamond,serif" }}>
              Milestones
            </h2>
            <div
              className="section-divider"
              style={{ margin: "1rem auto 0" }}
            />
          </div>
          <div
            style={{
              maxWidth: "700px",
              margin: "0 auto",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: "50%",
                top: 0,
                bottom: 0,
                width: "1px",
                background: "rgba(201,168,76,0.2)",
                transform: "translateX(-50%)",
              }}
            />
            {[
              {
                year: "2022",
                title: "Founded",
                desc: "NirmalRam Construction established in Nandurbar by Rambhau Patil.",
              },
              {
                year: "2022",
                title: "First Home",
                desc: "First residential project completed — a 2BHK dream home in Nandurbar.",
              },
              {
                year: "2023",
                title: "Government Contract",
                desc: "First government school infrastructure contract awarded and completed ahead of schedule.",
              },
              {
                year: "2024",
                title: "10+ Homes",
                desc: "Milestone of 10+ dream homes built. Team expanded with skilled engineers and workers.",
              },
              {
                year: "2025",
                title: "Growing",
                desc: "New commercial and premium residential projects underway across Nandurbar district.",
              },
            ].map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                style={{
                  display: "flex",
                  justifyContent: i % 2 === 0 ? "flex-start" : "flex-end",
                  marginBottom: "2rem",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    background: "#111",
                    border: "1px solid rgba(201,168,76,0.18)",
                    padding: "1.25rem 1.5rem",
                    borderRadius: "3px",
                    maxWidth: "280px",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.7rem",
                      color: "#C9A84C",
                      letterSpacing: "2px",
                      marginBottom: "0.35rem",
                    }}
                  >
                    {m.year}
                  </div>
                  <div style={{ fontWeight: 500, marginBottom: "0.35rem" }}>
                    {m.title}
                  </div>
                  <div
                    style={{
                      color: "#8A8070",
                      fontSize: "0.85rem",
                      lineHeight: 1.6,
                    }}
                  >
                    {m.desc}
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      [i % 2 === 0 ? "right" : "left"]: "-22px",
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      background: "#C9A84C",
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section">
        <div className="container">
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <span className="section-tag">Our Values</span>
            <h2>What We Stand For</h2>
            <div
              className="section-divider"
              style={{ margin: "1rem auto 0" }}
            />
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
              gap: "1.5rem",
            }}
          >
            {[
              {
                icon: "🏆",
                title: "Quality First",
                desc: "ISI-marked materials, proper mix ratios, and rigorous quality checks at every stage.",
              },
              {
                icon: "⏱️",
                title: "On-Time Delivery",
                desc: "We respect your time. Every project has a clear timeline we commit to.",
              },
              {
                icon: "💰",
                title: "Transparent Pricing",
                desc: "No hidden costs. Market-rate billing with full transparency on every rupee.",
              },
              {
                icon: "🤝",
                title: "Client Partnership",
                desc: "We involve you at every step. Your vision guides every decision we make.",
              },
              {
                icon: "🔬",
                title: "Technical Excellence",
                desc: "B.Tech-led team with modern construction techniques and safety standards.",
              },
            ].map((v) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                style={{
                  background: "#0E0E0E",
                  border: "1px solid rgba(201,168,76,0.1)",
                  padding: "1.75rem",
                  borderRadius: "3px",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>
                  {v.icon}
                </div>
                <h3 style={{ fontSize: "1rem", marginBottom: "0.5rem" }}>
                  {v.title}
                </h3>
                <p
                  style={{
                    color: "#5A5450",
                    fontSize: "0.85rem",
                    lineHeight: 1.6,
                  }}
                >
                  {v.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        style={{
          background: "linear-gradient(135deg,#0A0800,#1a1208,#0A0800)",
          borderTop: "1px solid rgba(201,168,76,0.15)",
          padding: "4rem 0",
          textAlign: "center",
        }}
      >
        <div className="container">
          <h2
            style={{
              fontFamily: "Cormorant Garamond,serif",
              marginBottom: "1rem",
            }}
          >
            Ready to Work With Us?
          </h2>
          <p
            style={{
              color: "#8A8070",
              maxWidth: "400px",
              margin: "0 auto 2rem",
              lineHeight: 1.8,
            }}
          >
            Let's build something remarkable together. Contact us for a free
            site visit and consultation.
          </p>
          <Link to="/contact" className="btn-gold">
            Start Your Project →
          </Link>
        </div>
      </section>
    </>
  );
}
